import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { formatCents } from "../../marketplace/shopApi";
import type { Product } from "../../marketplace/shopApi";
import { useShopCatalog } from "../../marketplace/useShopCatalog";
import { thumbUrl } from "../../../components/aotd-gallery/galleryImages";
import { useGetAotdByNumberQuery } from "../printsApi";
import type { AotdDay } from "../printsApi";
import { formatPriceCents } from "../printProducts";
import type { PrintFormat } from "../printProducts";
import { authorLabel, dayLabel, formatRaceOffset } from "../dayFormat";
import { MAX_QUANTITY, preorderErrorText, usePreorders } from "../usePreorders";
import { useGathering } from "../useGathering";
import { PromiseDialogHost } from "../promise/PromiseDialog";
import { PrintGallery } from "./PrintGallery";
import { OriginalPiece } from "./OriginalPiece";
import { MyPreorders } from "./MyPreorders";
import { GatheredCards } from "./GatheredCards";
import { FORMATS_BIG_FIRST, useToday } from "./useToday";
import "../../marketplace/storefront/storefront.css";
import "./todayPrints.css";

// /art-of-the-day/today/prints: today's AOTD and every print of it, laid out like a store
// product. Two columns on a wide screen (the print on the left; the day, the format, the price
// and PRE-ORDER on the right), one column on a phone. ?format= opens on one format, ?scanned=<n>
// says which printed QR brought the visitor here. Public; pre-ordering needs a sign-in.
// Trading cards aren't pre-ordered: a member gathers today's card and pre-orders a set at 21.

const NOT_ON_SALE = "not on sale yet. a pre-order is a promise to take it with your next order once prints go on sale. nothing is paid now.";
const REMOVED = "you removed your pre-order of this print, so it can't be pre-ordered again.";

const DayHead: React.FC<{ day: AotdDay }> = ({ day }) => {
  const meta = [
    day.authorOffsetMs !== null ? formatRaceOffset(day.authorOffsetMs) : null,
    day.engineVersion ? `aotd-${day.engineVersion}` : null,
  ].filter(Boolean);
  return (
    <header className="tp-dayhead">
      <p className="tp-label">
        <span>
          today&apos;s art of the day <b>#{day.number}</b>
        </span>
        <span>{dayLabel(day)}</span>
      </p>
      <h1 className="tp-title">{day.title}</h1>
      <p className="tp-by">
        by {authorLabel(day)}
        {meta.length > 0 && <span className="tp-meta"> · {meta.join(" · ")}</span>}
      </p>
    </header>
  );
};

/** Pick a format like a size, one big PRE-ORDER, and a "(n) pre-ordered" box beside it. */
const PreorderPanel: React.FC<{ day: AotdDay; products: Product[]; format: PrintFormat; setFormat: (f: PrintFormat) => void }> = ({
  day,
  products,
  format,
  setFormat,
}) => {
  const pre = usePreorders();
  const info = FORMATS_BIG_FIRST.find((f) => f.format === format)!;
  const held = pre.held(day.number, format, "print")?.quantity ?? 0;
  const removed = pre.locked(day.number, format, "print");
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    if (!justAdded) return;
    const t = setTimeout(() => setJustAdded(false), 1200);
    return () => clearTimeout(t);
  }, [justAdded]);

  return (
    <>
      <fieldset className="sf-field">
        <legend>format</legend>
        <div className="sf-chips">
          {FORMATS_BIG_FIRST.map((f) => (
            <label key={f.format} className={`sf-chip ${f.format === format ? "is-selected" : ""}`}>
              <input type="radio" name="format" checked={f.format === format} onChange={() => setFormat(f.format)} />
              {f.name}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="tp-price-row">
        <p className="sf-product-price">{formatCents(formatPriceCents(products, info, "print"), "EUR")}</p>
        <span className="tp-size">{info.size}</span>
      </div>
      {format === "trading-card" ? (
        <GatherRow day={day} />
      ) : (
        <>
          <div className="tp-buy-row">
            {pre.signedIn ? (
              <button
                type="button"
                className={`sf-buy ${justAdded ? "is-added" : ""}`}
                disabled={!pre.ready || pre.busy || held >= MAX_QUANTITY || removed}
                onClick={async () => {
                  if (await pre.setQuantity(day.number, format, "print", held + 1)) setJustAdded(true);
                }}
              >
                <span className="sf-stretch">{removed ? "REMOVED" : justAdded ? "ADDED" : "PRE-ORDER"}</span>
              </button>
            ) : (
              <Link to="/login" className="sf-buy">
                <span className="sf-stretch">SIGN IN TO PRE-ORDER</span>
              </Link>
            )}
            <a
              href="#tp-mine-title"
              className={`tp-slot ${held > 0 ? "has-items" : ""}`}
              aria-label={`${held} pre-ordered`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("tp-mine-title")?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <b>({held})</b>
              <span>pre-ordered</span>
            </a>
          </div>
          <p className="tp-note">{removed ? REMOVED : NOT_ON_SALE}</p>
          {pre.error ? <p className="tp-error">{preorderErrorText(pre.error)}</p> : null}
        </>
      )}
    </>
  );
};

/** The trading card: GATHER today's card, and an "n/21 gathered" box where the pre-order count is. */
const GatherRow: React.FC<{ day: AotdDay }> = ({ day }) => {
  const g = useGathering();
  const card = g.held(day.number, "print");
  const putBack = g.locked(day.number, "print");
  const count = g.cards.length;
  const toGo = Math.max(0, g.needed - count);

  const note = putBack
    ? "you put this card back, so it can't be gathered again."
    : card
      ? card.setId
        ? "gathered, and in a set you pre-ordered."
        : toGo > 0
          ? `gathered. ${toGo} more and you can pre-order them as a set.`
          : "gathered. you have enough for a set."
      : `cards aren't pre-ordered one by one: gather today's, one a day, and at ${g.needed} pre-order them as a set. nothing is paid now.`;

  return (
    <>
      <div className="tp-buy-row">
        {g.signedIn ? (
          <button
            type="button"
            className="sf-buy"
            disabled={!g.ready || g.busy || Boolean(card) || putBack}
            onClick={() => g.gather(day.number, "print")}
          >
            <span className="sf-stretch">{putBack ? "PUT BACK" : card ? "GATHERED" : "GATHER"}</span>
          </button>
        ) : (
          <Link to="/login" className="sf-buy">
            <span className="sf-stretch">SIGN IN TO GATHER</span>
          </Link>
        )}
        <a
          href="#tp-gathered-title"
          className={`tp-slot tp-slot--gathered ${count > 0 ? "has-items" : ""}`}
          aria-label={`${count} of ${g.needed} gathered`}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("tp-gathered-title")?.scrollIntoView({ behavior: "smooth" });
          }}
        >
          <b>
            {count}
            <small>/{g.needed}</small>
          </b>
          <span>gathered</span>
        </a>
      </div>
      <p className="tp-note">{note}</p>
      {count >= g.needed && (
        <button type="button" className="tp-btn tp-btn-holo tp-set-now" disabled={g.busy} onClick={() => g.preorderSet()}>
          pre-order the set of {count}
        </button>
      )}
      {g.error ? <p className="tp-error">{preorderErrorText(g.error)}</p> : null}
    </>
  );
};

/** A printed QR code brought the visitor here: say which print it was, then today's. */
const ScannedNote: React.FC<{ today: AotdDay }> = ({ today }) => {
  const { search } = useLocation();
  const n = Number(new URLSearchParams(search).get("scanned"));
  const valid = Number.isInteger(n) && n > 0;
  const { data } = useGetAotdByNumberQuery(n, { skip: !valid || n === today.number });
  if (!valid) return null;
  if (n === today.number) {
    return (
      <aside className="tp-scanned">
        <img src={thumbUrl(today.filename)} alt="" />
        <p>you scanned a print of today&apos;s art, #{today.number}.</p>
      </aside>
    );
  }
  if (!data) return null;
  return (
    <aside className="tp-scanned">
      <img src={thumbUrl(data.filename)} alt="" />
      <p>
        you scanned a print of <b>#{data.number}</b> &quot;{data.title}&quot; by {authorLabel(data)}, {dayLabel(data)}.
        <span> here is today&apos;s.</span>
      </p>
    </aside>
  );
};

const TodayPrints: React.FC = () => {
  const { data: day, error, refetch } = useToday();
  const { products } = useShopCatalog();
  const { search, hash } = useLocation();
  const asked = new URLSearchParams(search).get("format");
  const [format, setFormat] = useState<PrintFormat>(
    FORMATS_BIG_FIRST.some((f) => f.format === asked) ? (asked as PrintFormat) : "poster",
  );

  useEffect(() => {
    document.title = day ? `Prints of AOTD #${day.number} · NΛSOS` : "NΛSOS · prints";
  }, [day]);

  // "#tp-og-title" from the original's tile: the router doesn't scroll to anchors itself
  useEffect(() => {
    if (day && hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [day, hash]);

  return (
    <div className="sf-page tp-page">
      <nav className="sf-crumbs" aria-label="Breadcrumb">
        <Link to="/marketplace">store</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/collectibles">collectibles</Link>
        <span aria-hidden="true"> / </span>
        <span aria-current="page">today&apos;s prints</span>
      </nav>

      {day ? (
        <>
          <ScannedNote today={day} />
          <div className="tp-product">
            <div className="tp-left">
              <PrintGallery key={format} day={day} format={format} />
            </div>
            <div className="tp-info">
              <DayHead day={day} />
              <PreorderPanel day={day} products={products} format={format} setFormat={setFormat} />
            </div>
          </div>
          <OriginalPiece day={day} />
          <MyPreorders />
          <GatheredCards />
          <PromiseDialogHost />
        </>
      ) : error ? (
        <div className="sf-state">
          <p>couldn&apos;t load today&apos;s prints. try again in a bit.</p>
          <button type="button" className="sf-link-btn" onClick={() => refetch()}>
            try again
          </button>
        </div>
      ) : (
        <p className="sf-state">loading</p>
      )}
    </div>
  );
};

export default TodayPrints;
